import cn from 'classnames';
import { Icon } from '@pairy/pds-react';
import { AnimatePresence } from 'framer-motion';
import { MouseEvent } from 'react';
import { useAtom } from 'jotai';

import { isDisableHelpersAtom } from '../../../store/index.atom';

import * as Styled from './index.styles';

interface Props {
  isVisible: boolean;
  isMobile: boolean;
  onApply: () => void;
  onDismiss: () => void;
  translateKoToEn: (item: string) => string;
}

/**
 * 세로로 긴 슬라이드에서 가로 맞춤을 권유하는 플로팅 힌트 칩.
 * 화면을 가리는 딤 없이 슬라이드 영역 위에 얹히며,
 * 다른 플로팅 컨트롤과 동일하게 isDisableHelpers에 따라 흐려진다.
 */
const FitWidthHint = ({
  isVisible,
  isMobile,
  onApply,
  onDismiss,
  translateKoToEn,
}: Props) => {
  const [isDisableHelpers, setIsDisableHelpers] = useAtom(isDisableHelpersAtom);

  /* 흐려진 상태의 첫 클릭은 기능 실행 없이 컨트롤을 다시 켠다(V1 파리티) */
  const handleGuardClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDisableHelpers) {
      return;
    }
    e.stopPropagation();
    setIsDisableHelpers(false);
  };

  return (
    <Styled.ChipAnchor
      isMobile={isMobile}
      onClickCapture={handleGuardClickCapture}
      /* 칩 클릭이 뷰어의 헬퍼 토글로 전파되지 않게 한다 */
      onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      <AnimatePresence>
        {isVisible && (
          <Styled.Chip
            className={cn({ disabled: isDisableHelpers })}
            initial={{ opacity: 0 }}
            /*
             * framer-motion이 inline opacity를 소유하므로 흐림 처리를
             * CSS 클래스가 아니라 animate로 준다(모바일은 완전 숨김).
             */
            animate={{ opacity: isDisableHelpers ? (isMobile ? 0 : 0.25) : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
          >
            <Styled.ChipAction
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onApply();
              }}
            >
              <Icon
                icon="ph:arrows-out-line-horizontal"
                size={16}
                color="skyblue_500"
              />
              <Styled.ChipLabel variant="sm-medium" color="zinc_700">
                {translateKoToEn('fit-width-hint')}
              </Styled.ChipLabel>
            </Styled.ChipAction>

            <Styled.ChipClose
              type="button"
              aria-label={translateKoToEn('close')}
              title={translateKoToEn('close')}
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
            >
              <Icon icon="ph:x" size={14} color="zinc_500" />
            </Styled.ChipClose>
          </Styled.Chip>
        )}
      </AnimatePresence>
    </Styled.ChipAnchor>
  );
};

export default FitWidthHint;
